import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HREmployeeForm from './HRForm'; // Your existing form
import BulkEmployeeUpload from './UploadEmployeeDetails'; // New component

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HREmployeeFormWithTabs = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [seatStatus, setSeatStatus] = useState(null);
  const [loadingSeatStatus, setLoadingSeatStatus] = useState(true);

  const fetchSeatStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_URL}/api/alldetails/seat-limit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setSeatStatus(data.data);
      }
    } catch (err) {
      //console.error("Error fetching seat limit status:", err);
    } finally {
      setLoadingSeatStatus(false);
    }
  }, []);

  useEffect(() => {
    fetchSeatStatus();
  }, [fetchSeatStatus]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
   <Container maxWidth={false} sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
        Add Employee Details
      </Typography>

      {/* Tenant User Seat Status Banner */}
      {seatStatus && (
        <Box sx={{ mb: 3 }}>
          {seatStatus.isLimitReached || seatStatus.isExpired ? (
            <Alert 
              severity="warning" 
              variant="filled"
              sx={{ 
                borderRadius: 2,
                fontWeight: 'medium',
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  ⚠️ {seatStatus.isExpired 
                    ? "Trial Expired: Your organization trial has ended. Please upgrade your plan "
                    : `User Seat Limit Reached: ${seatStatus.usedSeats} of ${seatStatus.totalSeats} seats used. You cannot add or upload more employees under your current plan.`}
                </Typography>
                <Chip 
                  label={`${seatStatus.usedSeats} / ${seatStatus.totalSeats} Seats Used`} 
                  color="error" 
                  size="small" 
                  sx={{ fontWeight: 'bold', bgcolor: 'white', color: 'error.main' }}
                />
              </Box>
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f0f7ff', p: 1.5, borderRadius: 2, border: '1px solid #cce5ff' }}>
              <Typography variant="body2" color="primary" fontWeight="medium">
                <strong>Plan Seat Usage:</strong> {seatStatus.usedSeats} of {seatStatus.totalSeats} seats used ({seatStatus.remainingSeats} {seatStatus.remainingSeats === 1 ? 'seat' : 'seats'} available)
              </Typography>
              <Chip 
                label={`${seatStatus.remainingSeats} Left`} 
                color="primary" 
                size="small" 
                variant="outlined" 
              />
            </Box>
          )}
        </Box>
      )}

      <Paper elevation={3}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<PersonAddIcon />} 
            label="Add Single Employee" 
            iconPosition="start" 
          />
          <Tab 
            icon={<CloudUploadIcon />} 
            label="Bulk Upload" 
            iconPosition="start" 
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {currentTab === 0 && (
            <HREmployeeForm 
              seatStatus={seatStatus} 
              refreshSeatStatus={fetchSeatStatus} 
            />
          )}
          {currentTab === 1 && (
            <BulkEmployeeUpload 
              seatStatus={seatStatus} 
              refreshSeatStatus={fetchSeatStatus} 
            />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default HREmployeeFormWithTabs;